/**
 * stores/files.ts
 *
 * Files state management store
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useDatabase } from '@/composables/useDatabase'
import { useFolderUpdates } from '@/composables/useFolderUpdates'
import { usePlanStore } from '@/stores/plan'
import { logger } from '@/utils/logger'
import type { GComment, GFile } from '@/types/google'

export const useFilesStore = defineStore('files', () => {
    // State
    const files = ref<GFile[]>([])
    // Number of files before the plan limit was applied to `files`
    const totalFileCount = ref(0)
    const currentFolderId = ref<number | null>(null)

    // Actions
    async function fetchFiles (folderId: number, personId: string | null = null, forceReload = false) {
        // Skip if already loaded for the same folder
        if (currentFolderId.value === folderId && files.value.length > 0 && !forceReload) {
            return
        }

        try {
            const db = useDatabase()

            let query = db.files.where('folderId').equals(folderId)

            if (personId) {
                query = query.and(file => file.actors.includes(personId))
                logger.log('files: Filtering files by personId:', personId)
            }

            // eslint-disable-next-line unicorn/no-array-reverse
            const folderFiles = await query.reverse().sortBy('ts')

            // The Free plan only shows the latest updated files
            const planStore = usePlanStore()
            await planStore.whenPlanLoaded()
            const maxFiles = planStore.maxFiles
            totalFileCount.value = folderFiles.length
            files.value = maxFiles === null ? folderFiles : folderFiles.slice(0, maxFiles)
            currentFolderId.value = folderId
        } catch (error) {
            logger.error('Error fetching files:', error)
            throw error
        }
    }

    async function clearFiles (folderId: number) {
        const db = useDatabase()
        await db.files.where('folderId').equals(folderId).delete()
        files.value = []
        totalFileCount.value = 0
    }

    async function fetchFile (folderId: number, gid: string) {
        try {
            const db = useDatabase()
            const d = await db.files.get({ folderId, gid })
            return d
        } catch (error) {
            logger.error('Error fetching file:', error)
            throw error
        }
    }

    async function folderCount (folderId: number) {
        try {
            const db = useDatabase()
            return await db.files.where('folderId').equals(folderId).count()
        } catch (error) {
            logger.error('Error counting files:', error)
            throw error
        }
    }

    /** Mark a file as read, persisting to the database and updating any cached copy in `files` */
    async function markFileRead (folderId: number, gid: string) {
        try {
            const db = useDatabase()
            await db.files.update([folderId, gid], { isRead: true })

            const cached = files.value.find(file => file.folderId === folderId && file.gid === gid)
            if (cached) {
                cached.isRead = true
            }
        } catch (error) {
            logger.error('Error marking file as read:', error)
            throw error
        }
    }

    /**
     * Recompute the file's arcs after `comments` changed, persist `updates` and refresh the cached copy in `files`.
     * `forMeFiles`/`everythingElseFiles` in the folder page are computed from `files`, so they follow automatically.
     */
    async function updateFileArcs (folderId: number, gid: string, comments: GComment[]) {
        try {
            const db = useDatabase()
            const file = await db.files.get([folderId, gid])
            if (!file || !useFolderUpdates().updateFileArcsFromComments(file, comments)) {
                return
            }

            await db.files.update([folderId, gid], { updates: file.updates })

            const cached = files.value.find(f => f.folderId === folderId && f.gid === gid)
            if (cached) {
                cached.updates = file.updates
            }
        } catch (error) {
            logger.error('Error updating file arcs:', error)
            throw error
        }
    }

    // Resets in-memory state only (e.g. on logout); Dexie data is untouched
    function resetState () {
        files.value = []
        totalFileCount.value = 0
        currentFolderId.value = null
    }

    return {
        // State
        files,
        totalFileCount,
        // Actions
        resetState,
        fetchFiles,
        clearFiles,
        fetchFile,
        folderCount,
        markFileRead,
        updateFileArcs,
    }
})
